import { Box, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EditableBlock from './EditableBlock';
import EditableText from './EditableText';
import ImagePreview from './ImagePreview';

import { IMAGE_EDITOR_FIELDS, TEXT_EDITOR_FIELDS, BUTTON_EDITOR_FIELDS } from '../const';

const SplashLoginCard = ({
  isMobile,
  imageInfo,
  login,
  editable,
  onUpdateImageBlock,
  onUpdateLoginBlock,
  onUpdateMessagesBlock,
  selectedMessage = null,
  onUpdateMessageStyle = () => {},
  onSelectMessage = () => {},
}) => {
  const theme = useTheme();

  const messageColor = {
    borderColor: theme.palette.primary.redDark,
    backgroundColor: theme.palette.primary.redFourth,
  };

  // Terms link is a plain-text sibling of the welcome message, not independently editable,
  // so it must track the welcome message's font size/family to stay visually consistent.
  const handleWelcomeMessageStyleChange = style => {
    onUpdateLoginBlock('text.welcome.message.click', { style });

    const nextStyle = { ...login['text.terms.link'].style, fontSize: style?.fontSize, fontFamily: style?.fontFamily };
    onUpdateLoginBlock('text.terms.link', { style: nextStyle });
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: isMobile ? 380 : 470,
        backgroundColor: theme.palette.primary.white,
        borderRadius: theme.shape.customBorderRadius[1],
        boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.1)',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      {/* Logo */}
      {!!imageInfo?.logo?.show && (
        <EditableBlock
          editable={editable}
          editorTitle="Logo"
          editorFields={IMAGE_EDITOR_FIELDS}
          style={imageInfo?.logo?.style}
          onStyleChange={style => onUpdateImageBlock('logo', { style })}
          onClearIconClick={() => onUpdateImageBlock('logo', { style: {}, content: '' })}
          showClearIcon={!!imageInfo?.logo?.style?.content}
          blockSx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          {imageInfo?.logo?.style?.content ? (
            <ImagePreview alt="Logo" style={{ width: '200px', height: 'auto' }} base64={imageInfo?.logo?.style?.content} />
          ) : (
            <Box style={{ width: '200px', height: '40px' }} />
          )}
        </EditableBlock>
      )}

      {/* Message */}
      {selectedMessage && (
        <EditableBlock
          editable={editable}
          editorTitle={selectedMessage.label?.replace(/\b\w/g, char => char.toUpperCase()) || 'Message'} // Capitalize the first letter of each word
          editorFields={TEXT_EDITOR_FIELDS}
          style={selectedMessage.style}
          onStyleChange={nextStyle => onUpdateMessageStyle?.(selectedMessage.id, nextStyle)}
          blockSx={{ width: '100%' }}
          onSelect={() => onSelectMessage?.(selectedMessage.id)}
        >
          <Box
            sx={{
              width: '100%',
              border: `1px solid ${messageColor.borderColor}`,
              borderRadius: theme.shape.customBorderRadius[0],
              padding: '8px 12px',
              backgroundColor: messageColor.backgroundColor,
              textAlign: 'center',
            }}
          >
            <EditableText
              value={selectedMessage.text}
              editable={editable}
              onChange={html => onUpdateMessagesBlock(selectedMessage.id, { text: html })}
              component="div"
              sx={{
                fontSize: `${selectedMessage.style.fontSize}px`,
                fontFamily: selectedMessage.style.fontFamily,
                color: selectedMessage.style.fontColor,
                lineHeight: 1.5,
                outlineStyle: 'none',
              }}
            />
          </Box>
        </EditableBlock>
      )}

      {/* Title */}
      {!!login?.title?.show && (
        <EditableBlock
          editable={editable}
          editorTitle="Title"
          editorFields={TEXT_EDITOR_FIELDS}
          style={login.title.style}
          onStyleChange={style => onUpdateLoginBlock('title', { style })}
          blockSx={{ width: '100%' }}
        >
          <EditableText
            value={login?.title?.text}
            editable={editable}
            onChange={html => onUpdateLoginBlock('title', { text: html })}
            component="div"
            sx={{
              textAlign: 'center',
              fontSize: `${login.title.style.fontSize}px`,
              fontFamily: login.title.style.fontFamily,
              color: login.title.style.fontColor,
              fontWeight: theme.typography.customFontWeight[1],
              letterSpacing: '0.5px',
              padding: '8px 12px',
              outlineStyle: 'none',
            }}
          />
        </EditableBlock>
      )}

      {/* Placeholder - Username / Password (decorative preview only) */}
      {['placeholder.username', 'placeholder.password'].map(
        placeholderKey =>
          login?.[placeholderKey]?.show && (
            <TextField
              key={placeholderKey}
              sx={{
                width: '80%',
                '& .MuiInputBase-input': {
                  height: '10px',
                },
              }}
              placeholder={login?.[placeholderKey]?.text}
              InputProps={{ readOnly: true }}
            />
          )
      )}

      {/* Content */}
      {!!login['text.welcome.message.click']?.show && (
        <EditableBlock
          editable={editable}
          editorTitle="Welcome Message"
          editorFields={TEXT_EDITOR_FIELDS}
          style={login['text.welcome.message.click'].style}
          onStyleChange={handleWelcomeMessageStyleChange}
          blockSx={{ width: '80%' }}
        >
          <Box
            sx={{
              width: '100%',
              borderRadius: theme.shape.customBorderRadius[0],
              padding: '6px 0px',
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            <EditableText
              value={login?.['text.welcome.message.click']?.text}
              editable={editable}
              onChange={html => onUpdateLoginBlock('text.welcome.message.click', { text: html })}
              component="span"
              sx={{
                fontSize: `${login['text.welcome.message.click'].style.fontSize}px`,
                fontFamily: login['text.welcome.message.click'].style.fontFamily,
                color: login['text.welcome.message.click'].style.fontColor,
                outlineStyle: 'none',
              }}
            />

            <EditableText
              value={login?.['text.terms.link']?.text}
              editable={false}
              component="span"
              sx={{
                fontSize: `${login['text.terms.link'].style.fontSize}px`,
                fontFamily: login['text.terms.link'].style.fontFamily,
                color: login['text.terms.link'].style.fontColor,
                textDecoration: editable ? 'none' : 'underline',
                cursor: editable ? 'default' : 'pointer',
                outlineStyle: 'none',
              }}
            />
          </Box>
        </EditableBlock>
      )}

      {/* Button */}
      {!!login['button.continue']?.show && (
        <EditableBlock
          editable={editable}
          editorTitle="Action Button"
          editorFields={BUTTON_EDITOR_FIELDS}
          style={login['button.continue'].style}
          onStyleChange={style => onUpdateLoginBlock('button.continue', { style })}
          blockSx={{ width: '80%' }}
        >
          <Box
            sx={{
              width: '100%',
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px',
              backgroundColor: login['button.continue'].style.buttonColor,
            }}
          >
            <EditableText
              value={login?.['button.continue']?.text}
              editable={editable}
              onChange={html => onUpdateLoginBlock('button.continue', { text: html })}
              component="div"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              sx={{
                fontSize: theme.typography.customFontSize[2],
                fontFamily: login['button.continue'].style.fontFamily,
                color: login['button.continue'].style.fontColor,
                fontWeight: theme.typography.customFontWeight[1],
                width: '100%',
                textAlign: 'center',
                outlineStyle: 'none',
                cursor: editable ? 'text' : 'pointer',
              }}
            />
          </Box>
        </EditableBlock>
      )}
    </Box>
  );
};

export default SplashLoginCard;
